import React, { useMemo, useState } from 'react';
import { ArrowClockwise, CircleNotch, Copy, PenNib, Play, Trash, WarningCircle } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, LeerZustand, SegmentedControl, type Segment } from '../../../../components/immobilien/ui';
import { passtRubrik, rubrikenAus } from '../../../../lib/dokumente/signaturFilter';
import { unterzeichnerFarbe } from '../../../../lib/dokumente/signatur';
import { kopiereAlsEntwurf, loescheVorgang } from '../../../../lib/dokumente/signaturApi';
import { useSignaturVorgaenge } from '../../../../lib/dokumente/useSignaturVorgaenge';
import type { SignaturVorgang } from '../../../../lib/dokumente/signaturTypen';

const ALLE = '__alle';
const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);

/** Signatur-Vorlagen (Handoff Dokumente §6.5, Screen 11): Rubrik-Filter, Karten mit Rollen, „Vorgang starten“. */
export function SignaturVorlagen({ userId, onEditor, onMeldung, mobil }: {
  userId: string | null; onEditor: (id: string) => void; onMeldung: (text: string, fehler?: boolean) => void; mobil: boolean;
}) {
  const { liste, fehler, bilder, neuLaden } = useSignaturVorgaenge();
  const vorlagen = useMemo(() => (liste ?? []).filter(v => v.ist_vorlage), [liste]);
  const [rubrik, setRubrik] = useState<string>(ALLE);
  const [startet, setStartet] = useState<string | null>(null);
  const [loeschen, setLoeschen] = useState<SignaturVorgang | null>(null);
  const [loescht, setLoescht] = useState(false);

  const rubriken = rubrikenAus(vorlagen);
  const segmente: Segment<string>[] = [
    { key: ALLE, label: 'Alle', anzahl: vorlagen.length },
    ...rubriken.map(r => ({ key: r, label: r, anzahl: vorlagen.filter(v => passtRubrik(v, r)).length })),
  ];
  const sichtbar = vorlagen.filter(v => passtRubrik(v, rubrik === ALLE ? null : rubrik));

  const starte = async (v: SignaturVorgang) => {
    setStartet(v.id);
    try { const neu = await kopiereAlsEntwurf(v, { mitEmail: false, userId }); onEditor(neu.id); }
    catch (e) { onMeldung(fehlerText(e, 'Vorgang konnte nicht angelegt werden'), true); }
    finally { setStartet(null); }
  };
  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLoescht(true);
    try { await loescheVorgang(loeschen); onMeldung(`Vorlage „${loeschen.titel}“ gelöscht`); neuLaden(); }
    catch (e) { onMeldung(fehlerText(e, 'Löschen fehlgeschlagen'), true); }
    finally { setLoescht(false); setLoeschen(null); }
  };

  return (
    <section className="flex flex-col min-w-0" style={{ gap: 12 }} aria-label="Signatur-Vorlagen">
      <h2 className="m-0" style={{ fontSize: 22, fontWeight: 800 }}>Signatur-Vorlagen</h2>
      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      ) : !liste ? (
        <div className="animate-pulse" style={{ height: 180, borderRadius: 26, background: 'var(--fill2)' }} aria-busy="true" />
      ) : vorlagen.length === 0 ? (
        <LeerZustand icon={Copy} titel="Noch keine Signatur-Vorlagen" text="Im Editor „Als Vorlage speichern“ wählen, um einen Vorgang als Vorlage abzulegen." />
      ) : (
        <>
          {rubriken.length > 0 && (
            <div className="immo-scroll" style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <SegmentedControl ariaLabel="Rubrik" art="akzent" wert={rubrik} onChange={setRubrik} segmente={segmente} hoehe={mobil ? 44 : 34} className="flex-nowrap" />
            </div>
          )}
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 14 }}>
            {sichtbar.map(v => (
              <article key={v.id} className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 16, gap: 12 }} aria-label={v.titel}>
                <button type="button" onClick={() => onEditor(v.id)} className="immo-hover-flaeche flex items-center text-left cursor-pointer min-w-0"
                  title="Vorlage bearbeiten" style={{ gap: 12, border: 'none', background: 'transparent', color: 'var(--ink)', padding: 4, margin: -4, borderRadius: 16 }}>
                  {bilder[v.id]
                    ? <img src={bilder[v.id]} alt="" style={{ width: 44, height: 56, borderRadius: 10, objectFit: 'cover', objectPosition: 'top', background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)', flexShrink: 0 }} />
                    : <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--acc-soft)' }}><PenNib weight="fill" size={20} color="var(--acc)" /></span>}
                  <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
                    <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{v.titel}</span>
                    <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{v.unterzeichner.length} Unterzeichner · {v.felder.length} Felder</span>
                  </span>
                </button>
                <div className="flex flex-wrap" style={{ gap: 6 }}>
                  {v.vorlage_kategorie && <span style={{ height: 26, padding: '0 10px', borderRadius: 13, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>{v.vorlage_kategorie}</span>}
                  {v.unterzeichner.map((u, i) => (
                    <span key={i} className="flex items-center" style={{ height: 26, padding: '0 10px', borderRadius: 13, gap: 6, fontSize: 11, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: unterzeichnerFarbe(i) }} />{u.name || `Unterzeichner ${i + 1}`}
                    </span>
                  ))}
                </div>
                <div className="flex" style={{ gap: 8, marginTop: 'auto' }}>
                  <ImmoButton art="akzent" groesse={44} pille={false} icon={startet === v.id ? CircleNotch : Play} disabled={!!startet} onClick={() => void starte(v)} style={{ flex: 1 }}>Vorgang starten</ImmoButton>
                  <button type="button" aria-label={`Vorlage „${v.titel}“ löschen`} title="Vorlage löschen" onClick={() => setLoeschen(v)} className="immo-hover-flaeche flex items-center justify-center cursor-pointer shrink-0"
                    style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--bad)' }}><Trash weight="bold" size={15} /></button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
      {loeschen && (
        <BestaetigenDialog titel="Vorlage löschen?" text="Die Vorlage wird unwiderruflich gelöscht. Bereits gestartete Vorgänge bleiben erhalten." detail={<strong>{loeschen.titel}</strong>}
          bestaetigenLabel="Löschen" laeuft={loescht} onBestaetigen={() => void bestaetigeLoeschen()} onAbbrechen={() => setLoeschen(null)} />
      )}
    </section>
  );
}
